/**
 * Share + persistence helpers for the Field of Play.
 *
 * A field is shared as a URL-safe, self-contained string (no backend needed):
 * the placed tokens are JSON-encoded, then base64url-encoded so the whole
 * situation travels in a link. `encodeField` / `decodeField` are pure and
 * isomorphic (browser + node), so they are unit-testable. Cycle snapshots are
 * named fields kept in localStorage; that layer lives in the page.
 */

import type { PlacedToken, StageId, LevelId, TokenType } from '../data/fieldOfPlay';
import { STAGE_ORDER, LEVELS, TOKEN_DEFS } from '../data/fieldOfPlay';

const VALID_STAGES = new Set<StageId>(STAGE_ORDER);
const VALID_LEVELS = new Set<LevelId>(LEVELS.map((l) => l.id));
const VALID_TYPES = new Set<TokenType>(TOKEN_DEFS.map((d) => d.type));

function toBase64Url(input: string): string {
  const bytes = new TextEncoder().encode(input);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(input: string): string {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Encode a field into a compact, URL-safe string. */
export function encodeField(tokens: PlacedToken[]): string {
  return toBase64Url(JSON.stringify({ v: 1, tokens }));
}

/** Coerce one unknown value into a valid PlacedToken, or null if unusable. */
function sanitizeToken(raw: unknown, index: number): PlacedToken | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!VALID_TYPES.has(r.type as TokenType)) return null;
  if (!VALID_STAGES.has(r.stage as StageId)) return null;
  if (!VALID_LEVELS.has(r.level as LevelId)) return null;

  const opt = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined =>
    typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;

  return {
    id: typeof r.id === 'string' && r.id ? r.id : `shared-${index}-${Math.random().toString(36).slice(2, 7)}`,
    type: r.type as TokenType,
    label: typeof r.label === 'string' ? r.label : '',
    stage: r.stage as StageId,
    level: r.level as LevelId,
    priority: opt(r.priority, ['low', 'med', 'high'] as const),
    confidence: opt(r.confidence, ['low', 'med', 'high'] as const),
    reversibility: opt(r.reversibility, ['reversible', 'irreversible'] as const),
    owner: typeof r.owner === 'string' && r.owner.trim() ? r.owner : undefined,
  };
}

/** Decode a shared string back into tokens, dropping anything malformed. Returns null when the payload is unreadable. */
export function decodeField(encoded: string): PlacedToken[] | null {
  if (!encoded || !encoded.trim()) return null;
  try {
    const parsed = JSON.parse(fromBase64Url(encoded.trim())) as unknown;
    const rawTokens =
      Array.isArray(parsed) ? parsed : Array.isArray((parsed as { tokens?: unknown }).tokens) ? (parsed as { tokens: unknown[] }).tokens : null;
    if (!rawTokens) return null;
    const tokens = rawTokens
      .map((t, i) => sanitizeToken(t, i))
      .filter((t): t is PlacedToken => t !== null);
    return tokens;
  } catch {
    return null;
  }
}
